/**
 * Optional saved Contour+ model catalogue for the agent's model-selection
 * tools. Used only when the live list (fetched from the cloud through the
 * backend or connector) is not loaded — e.g. a connector running without
 * cloud credentials. Off unless `agentModelCatalogueUrl` is configured; the
 * live list always takes precedence. The catalogue holds model names and
 * body parts only, never patient data.
 */
import type { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';

type Fetch = (url: string) => Promise<{
    ok: boolean;
    json(): Promise<unknown>;
}>;

function isPredictionModel(value: unknown): value is PredictionModel {
    const m = value as Partial<PredictionModel> | null;
    return (
        typeof m === 'object' &&
        m !== null &&
        typeof m.name === 'string' &&
        m.name.length > 0 &&
        typeof m.label === 'string' &&
        typeof m.bodyPart === 'string' &&
        Array.isArray(m.inputModalities)
    );
}

/**
 * Fetch and validate the saved catalogue. Undefined when the URL is empty,
 * the request fails, or the file is not a non-empty `PredictionModel[]` —
 * the agent then reports the model list as not loaded, exactly as without
 * the fallback.
 */
export async function loadFallbackCatalogue(
    url: string,
    fetchFn: Fetch = (u) => fetch(u)
): Promise<ReadonlyArray<PredictionModel> | undefined> {
    if (!url) {
        return undefined;
    }
    try {
        const response = await fetchFn(url);
        if (!response.ok) {
            return undefined;
        }
        const data = await response.json();
        if (
            !Array.isArray(data) ||
            data.length === 0 ||
            !data.every(isPredictionModel)
        ) {
            console.warn(
                '[agent] agentModelCatalogueUrl is not a Contour+ catalogue ' +
                    '(PredictionModel[]); ignoring it'
            );
            return undefined;
        }
        return data;
    } catch {
        return undefined;
    }
}

/**
 * The live list when it has models, otherwise the saved catalogue. Without a
 * saved catalogue the live value is returned unchanged (including `[]` or
 * `undefined`), so behaviour is identical to having no fallback at all.
 */
export function chooseContourModels(
    live: ReadonlyArray<PredictionModel> | undefined,
    fallback: ReadonlyArray<PredictionModel> | undefined
): ReadonlyArray<PredictionModel> | undefined {
    if (live && live.length > 0) {
        return live;
    }
    return fallback ?? live;
}
